import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Truck,
  BarChart2,
  Search,
  Clock,
  Shield,
  Zap,
  ArrowRight,
  Database,
  Settings,
  Users,
  GitBranch,
  MessageSquare,
  FileText,
  AlertCircle,
  CheckCircle,
  TrendingUp
} from 'lucide-react';

// Reusable components
const FeatureCard = ({ icon: Icon, title, description }) => (
    <motion.div
      className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors flex flex-col items-center text-center"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </motion.div>
);

const StatisticCard = ({ value, label, icon: Icon, trend }) => (
    <motion.div
      className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col items-center text-center"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="flex flex-col items-center gap-4 mb-4">
        <div className="p-3 rounded-lg bg-indigo-500/10">
          <Icon className="w-6 h-6 text-indigo-400" />
        </div>
        {trend && (
          <div className="bg-green-500/10 px-2 py-1 rounded-full flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm">{trend}</span>
          </div>
        )}
      </div>
      <div className="text-3xl font-bold text-white mb-2">{value}</div>
      <p className="text-gray-400">{label}</p>
    </motion.div>
);

const LoadManagement = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-indigo-900 to-purple-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Intelligent Load Management
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              AI-powered load matching and optimization platform designed for enterprise-scale operations
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatisticCard
              icon={Truck}
              value="1M+"
              label="Loads Processed Monthly"
              trend="+12.5%"
            />
            <StatisticCard
              icon={Clock}
              value="<2min"
              label="Average Match Time"
              trend="-35%"
            />
            <StatisticCard
              icon={BarChart2}
              value="99.9%"
              label="Match Accuracy"
              trend="+2.3%"
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Enterprise-Grade Features
            </h2>
            <p className="text-gray-300">
              Comprehensive tools for managing loads at scale
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Search}
              title="Smart Load Matching"
              description="AI-powered matching algorithm considering multiple factors including location, capacity, and historical performance."
            />
            <FeatureCard
              icon={Database}
              title="Real-Time Analytics"
              description="Live insights into load performance, carrier metrics, and market conditions for data-driven decisions."
            />
            <FeatureCard
              icon={Shield}
              title="Risk Management"
              description="Automated risk assessment and mitigation strategies for every load and carrier."
            />
            <FeatureCard
              icon={Settings}
              title="Custom Workflows"
              description="Configurable approval processes and routing rules tailored to your organization."
            />
            <FeatureCard
              icon={Users}
              title="Team Collaboration"
              description="Built-in tools for team coordination and load management across departments."
            />
            <FeatureCard
              icon={GitBranch}
              title="Integration Ready"
              description="RESTful APIs and webhooks for seamless integration with existing systems."
            />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Streamlined Load Management
            </h2>
            <p className="text-gray-300">
              End-to-end visibility and control over your logistics operations
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {[
              {
                icon: FileText,
                title: "Load Creation & Planning",
                description: "Intelligent load building with automated optimization and validation."
              },
              {
                icon: Zap,
                title: "Real-Time Matching",
                description: "Instant carrier matching based on capacity, location, and performance metrics."
              },
              {
                icon: MessageSquare,
                title: "Communication & Updates",
                description: "Integrated messaging and status updates throughout the delivery lifecycle."
              },
              {
                icon: AlertCircle,
                title: "Exception Management",
                description: "Proactive issue detection and resolution workflow management."
              },
              {
                icon: CheckCircle,
                title: "Completion & Analytics",
                description: "Automated documentation and performance analytics post-delivery."
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="relative flex items-start mb-12 last:mb-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                {index < 4 && (
                  <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-indigo-500/50 to-transparent" />
                )}
                <div className="relative z-10 rounded-full p-4 bg-indigo-500/20 border border-indigo-500/50">
                  <step.icon className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="ml-8">
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-300">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Load Management?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join industry leaders already using our platform to optimize their operations
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Get Started Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LoadManagement;